import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';

import { UiNewIndicatorComponent } from '../new-indicator/new-indicator.component';
import type { AvatarColor } from './avatar.types';
import { avatarInitials, avatarColor } from './avatar.utils';

const LIGHT = 'text-domain-acct-avatar-fg-light';
const DARK = 'text-domain-acct-avatar-fg-dark';

const COLOR_CLASSES: Record<AvatarColor, string> = {
  1: `bg-domain-acct-avatar-1-bg ${LIGHT}`,
  2: `bg-domain-acct-avatar-2-bg ${DARK}`,
  3: `bg-domain-acct-avatar-3-bg ${LIGHT}`,
  4: `bg-domain-acct-avatar-4-bg ${LIGHT}`,
  5: `bg-domain-acct-avatar-5-bg ${LIGHT}`,
  6: `bg-domain-acct-avatar-6-bg ${LIGHT}`,
  7: `bg-domain-acct-avatar-7-bg ${DARK}`,
  8: `bg-domain-acct-avatar-8-bg ${LIGHT}`,
  9: `bg-domain-acct-avatar-9-bg ${LIGHT}`,
  10: `bg-domain-acct-avatar-10-bg ${LIGHT}`,
  11: `bg-domain-acct-avatar-11-bg ${LIGHT}`,
  12: `bg-domain-acct-avatar-12-bg ${DARK}`,
  13: `bg-domain-acct-avatar-13-bg ${DARK}`,
  14: `bg-domain-acct-avatar-14-bg ${LIGHT}`,
  15: `bg-domain-acct-avatar-15-bg ${LIGHT}`,
  16: `bg-domain-acct-avatar-16-bg ${DARK}`,
  17: `bg-domain-acct-avatar-17-bg ${DARK}`,
  18: `bg-domain-acct-avatar-18-bg ${DARK}`,
  19: `bg-domain-acct-avatar-19-bg ${LIGHT}`,
  20: `bg-domain-acct-avatar-20-bg ${DARK}`,
  21: `bg-domain-acct-avatar-21-bg ${LIGHT}`,
};

const BASE_CLASSES =
  'relative box-border inline-flex size-8 shrink-0 items-center justify-center rounded-full whitespace-nowrap text-interactive-large select-none';

/**
 * @cabooseInclude
 *
 * A circle with initials, filled with one of 21 colors. The initials come
 * from `name`, and the color from `seed`, so the same account, company, or
 * person always gets the same color.
 *
 * Set `dot` to flag the avatar with a `ui-new-indicator`. Pass
 * `customBackground` and `customForeground` for a white-label agency's own
 * colors.
 *
 * ```html
 * <ui-avatar
 *   [name]="agency.name"
 *   [seed]="agency.id" />
 * ```
 */
@Component({
  selector: 'ui-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiNewIndicatorComponent],
  host: {
    role: 'img',
    '[attr.aria-label]': 'name()',
    '[attr.title]': 'name()',
    '[class]': 'hostClass()',
    '[style.background-color]': 'customColors()?.background',
    '[style.color]': 'customColors()?.foreground',
  },
  template: `
    {{ initials() }}
    @if (dot()) {
      <ui-new-indicator class="absolute top-0 left-6" />
    }
  `,
})
export class UiAvatarComponent {
  /** The name it represents. Sets the initials and the accessible name. */
  public readonly name = input.required<string>();

  /**
   * Picks one of 21 colors. Pass a stable value such as an account, company,
   * or user ID, so the color never changes.
   */
  public readonly seed = input.required<string | number>();

  /** Shows one of the 21 colors in place of the seeded one. */
  public readonly color = input<AvatarColor | undefined>(undefined);

  /** Shows a new indicator in the top right corner. */
  public readonly dot = input(false, { transform: booleanAttribute });

  /**
   * A white-label agency's background color. Overrides the seeded color only
   * when `customForeground` is also set.
   */
  public readonly customBackground = input<string | undefined>(undefined);

  /**
   * A white-label agency's text color. Overrides the seeded color only when
   * `customBackground` is also set.
   */
  public readonly customForeground = input<string | undefined>(undefined);

  protected readonly initials = computed(() => avatarInitials(this.name()));

  protected readonly customColors = computed(() => {
    const background = this.customBackground();
    const foreground = this.customForeground();
    return background && foreground ? { background, foreground } : null;
  });

  protected readonly hostClass = computed(() =>
    this.customColors()
      ? BASE_CLASSES
      : `${BASE_CLASSES} ${COLOR_CLASSES[this.color() ?? avatarColor(this.seed())]}`
  );
}
